import {useMemo} from 'react'

import {
    GRAPH_LABEL_CODE_TO_NAME,
    GRAPH_LABEL_ORDER,
    실손의료_코드,
    실손의료_코드_목록,
    실손의료_코드_이름,
    실손의료_코드_이름_ORDER,
} from '$constants/insurance/diagnosis'
import useDetailCoveragesQuery from '$hooks/insurance/coverages/useDetailCoveragesQuery'
import {usePeerAverageBenefitAmountQuery} from '$hooks/insurance/my/home/resource/usePeerAverageBenefitAmount'
import {usePeerAverageBenefitRankQuery} from '$hooks/insurance/my/home/resource/usePeerAverageBenefitRankQuery'
import {GRAPH_LABEL_LIST, 보장분류_카테고리} from '$share/constants/insurance'
import {CoverageResponse} from '$share/types/insurance'
import {get또래대비보장조건} from '$utils/insurance'

export function getMyCoverages(coverages: CoverageResponse) {
    const labelMap = Object.fromEntries(GRAPH_LABEL_LIST.map((labelName) => [labelName, 0]))

    coverages.forEach((coverage) => {
        const {benefitAmt, benefitCode} = coverage
        const labelName = GRAPH_LABEL_CODE_TO_NAME[benefitCode]

        if (typeof labelMap[labelName] === 'number') {
            labelMap[labelName] += benefitAmt
        }
    })

    return Object.entries(labelMap)
        .sort((a, b) => GRAPH_LABEL_ORDER[a[0]] - GRAPH_LABEL_ORDER[b[0]])
        .map((elem) => ({labelName: elem[0], myAmount: elem[1]}))
}

// 혹시모를 에러를 대비해서 가드문
const getMaxAmount = (
    index: number,
    myElem: {
        labelName: string
        myAmount: number
    },
    rankingList: {
        maxAmount: number
        myRank: number
    }[],
    peerCoverages: {
        labelName: string
        peerAmount: number
    }[],
) => {
    if (rankingList.length === 0 || peerCoverages.length === 0) {
        return 0
    }

    return Math.max(rankingList[index].maxAmount, Math.max(myElem.myAmount, peerCoverages[index].peerAmount))
}

export function useDiagnosisCoverage() {
    const {data: my = []} = useDetailCoveragesQuery()
    const {data: peer = []} = usePeerAverageBenefitAmountQuery()

    const myCoverages = useMemo(() => getMyCoverages(my), [my])

    const {data: ranking = []} = usePeerAverageBenefitRankQuery({
        benefitAmounts: myCoverages.map((elem) => Math.trunc(elem.myAmount / 10_000)).join(','),
    })

    const peerCoverages = useMemo(() => {
        return peer
            .sort((a, b) => GRAPH_LABEL_ORDER[a.benefitName] - GRAPH_LABEL_ORDER[b.benefitName])
            .map((elem) => ({labelName: elem.benefitName, peerAmount: elem.avgAmt}))
    }, [peer])

    const rankingList = useMemo(
        () =>
            ranking
                .sort((a, b) => GRAPH_LABEL_ORDER[a.benefitName] - GRAPH_LABEL_ORDER[b.benefitName])
                .map((elem) => ({maxAmount: elem.maxAmount, myRank: elem.myRank})),
        [ranking],
    )

    /**
     * 그래프 및 3대 주요 질병에 사용
     */
    const coverages = useMemo(() => {
        return myCoverages.map((myElem, index) => ({
            ...myElem,
            ...peerCoverages[index],
            ...rankingList[index],
            /**
             * https://oss.fin.navercorp.com/Product-Planning/Myasset-QA/issues/1725
             * 그래프의 기준이 되는 최대 값은 이쁜 모양을 위해 보장금액 상위 20%를 기준으로 함
             * 그런데 만약 편차가 극단적으로 커서 최상위의 값이 극도로 크고 나머지가 엄청 작다면
             * 상위 20%보다 평균의 값이 더 큰 현상이 발생할 수 있음
             * 이런 경우를 고려해서 최대 값은 기본적으로 그래프의 형상을 위해 상위 20%를 사용하지만 이런 지표의 왜곡이 발생한다면
             * 내 보장이나 또래 보장을 최대 값으로 사용
             */
            maxAmount: getMaxAmount(index, myElem, rankingList, peerCoverages),
            condition: get또래대비보장조건(myElem.myAmount, peerCoverages[index]?.peerAmount),
        }))
    }, [myCoverages, peerCoverages, rankingList])

    /**
     * 실손의료비 영역에 사용
     */
    const 실손의료비 = useMemo(() => {
        const benefitMap = Object.fromEntries(Object.entries(실손의료_코드).map(([name]) => [name, 0]))

        my.filter((elem) => 실손의료_코드_목록.includes(elem.benefitCode)).forEach((elem) => {
            const name = 실손의료_코드_이름[elem.benefitCode]
            if (typeof benefitMap[name] === 'number') {
                const currentAmount = benefitMap[name]
                benefitMap[name] = Math.max(currentAmount, elem.benefitAmt)
            }
        })

        return Object.entries(benefitMap)
            .sort((a, b) => 실손의료_코드_이름_ORDER[a[0]] - 실손의료_코드_이름_ORDER[b[0]])
            .map((elem) => ({benefitName: elem[0], benefitAmount: elem[1]}))
    }, [my])

    const 통원보장_여부 = useMemo(() => my.some((elem) => elem.category === 보장분류_카테고리.통원), [my])
    const 입원보장_여부 = useMemo(() => my.some((elem) => elem.category === 보장분류_카테고리.입원), [my])

    return useMemo(
        () => ({
            실손의료비,
            coverages,
            myAmountList: myCoverages.map((elem) => elem.myAmount),
            peerAmountList: peerCoverages.map((elem) => elem.peerAmount),
            통원보장_여부,
            입원보장_여부,
        }),
        [coverages, myCoverages, peerCoverages, 실손의료비, 입원보장_여부, 통원보장_여부],
    )
}

export type GraphCoverages = ReturnType<typeof useDiagnosisCoverage>['coverages']
